import { Box, Button, Flex, Heading, Icon, Link, SimpleGrid, Spinner, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useState } from "react";
import { FaDownload } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  ContractStatusDto,
  ContractValueDto,
  ProcurementPhaseDto,
  ProjectDto,
  useGetProjectProcurementSummaryQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { ListLoadError } from "../../../common/ListLoadError";
import { displaySum } from "../../../common/formatting/displaySum";
import { BUCKET_META, BucketKey, bucketQuery } from "../../../common/layout/statusBuckets";
import { useNavLabel } from "../../../common/layout/useNavLabel";
import { urls } from "../../../urls";
import { contractRegisterLinkFromProject } from "../../contracts/view-all/FromProjectLine";
import { downloadProcurementSummary } from "./downloadProcurementSummary";
import { REVEAL_PROCUREMENT_LIST } from "./procurementListReveal";

export const PHASE_BUCKET: Record<ProcurementPhaseDto, BucketKey> = {
  [ProcurementPhaseDto.InProgress]: "arbeid",
  [ProcurementPhaseDto.Announced]: "kunngjort",
  [ProcurementPhaseDto.Evaluation]: "evaluering",
  [ProcurementPhaseDto.Award]: "tildeling",
  [ProcurementPhaseDto.Completed]: "fullfort",
  [ProcurementPhaseDto.Cancelled]: "avlyst",
};

const projectTabLink = (projectId: string, query: string) => `${urls.app.projects.view.go(projectId)}?${query}`;

export const ProcurementSummary = ({ project }: { project: ProjectDto }) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const displayer = useApiError();
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    data: summary,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetProjectProcurementSummaryQuery({ projectId: project.id });

  const download = async () => {
    setIsDownloading(true);
    try {
      await downloadProcurementSummary({
        projectId: project.id,
        fileName: `${project.name} - ${t("Procurement in the project")}`,
      });
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const contracts = summary?.contracts;
  const projectAndSubprojects = [project.id, ...(contracts?.subprojectIds ?? [])];

  return (
    <Box as="section" bg="smBackground" border="1px solid" borderColor="smBorder" rounded="lg" p="4" mb="4">
      <Flex justify="space-between" align="center" gap="2" wrap="wrap">
        <Heading as="h2" size="sm" color="smText">
          {t("Procurement in the project")}
        </Heading>
        <Button
          size="sm"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaDownload} />}
          onClick={download}
          isLoading={isDownloading}
        >
          {t("Download")}
        </Button>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="3">
        {t("Based on what you have access to")}
      </Text>
      {isLoading && <Spinner color="smPrimary" />}
      {isError && (
        <ListLoadError
          message={t("The overview of the project's procurement could not be loaded right now.")}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      )}
      {summary && (
        <>
          {contracts && (
            <>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing="3">
                <ContractFigure
                  label={t("Contracts in the project")}
                  value={contracts.inProject}
                  to={contractRegisterLinkFromProject(project.id, { projectIds: [project.id] })}
                />
                {contracts.subprojectIds.length > 0 && (
                  <ContractFigure
                    label={t("Contracts in subprojects")}
                    value={contracts.inSubprojects}
                    to={contractRegisterLinkFromProject(project.id, { projectIds: contracts.subprojectIds })}
                  />
                )}
                {contracts.expiringBeforeProjectEnd ? (
                  <Figure
                    label={t("Expires before the project ends")}
                    figure={`${contracts.expiringBeforeProjectEnd.count}`}
                    to={contractRegisterLinkFromProject(project.id, {
                      projectIds: projectAndSubprojects,
                      statuses: [ContractStatusDto.Active],
                      endDateTo: contracts.expiringBeforeProjectEnd.endingOnOrBefore,
                    })}
                  />
                ) : (
                  <Box border="1px solid" borderColor="smBorder" rounded="md" p="3">
                    <Text fontSize="sm" color="smMuted">
                      {t("The project has no end date")}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </>
          )}
          {summary.procurementsByPhase && (
            <Box mt="4">
              <Text fontSize="sm" fontWeight="semibold" color="smText" mb="1">
                {t("Procurements by phase")}
              </Text>
              <Wrap spacing="2">
                {summary.procurementsByPhase.map(({ phase, count }) => (
                  <WrapItem key={phase}>
                    <Link
                      as={RouterLink}
                      to={projectTabLink(project.id, `tab=sourcing-events&${bucketQuery(PHASE_BUCKET[phase])}`)}
                      state={REVEAL_PROCUREMENT_LIST}
                      fontSize="sm"
                      color="smPrimary"
                      border="1px solid"
                      borderColor="smBorder"
                      rounded="md"
                      px="2"
                      py="1"
                    >
                      {`${navLabel(BUCKET_META[PHASE_BUCKET[phase]].labelKey)}: ${count}`}
                    </Link>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
          <Link
            as={RouterLink}
            to={projectTabLink(project.id, "tab=products")}
            display="inline-block"
            mt="4"
            fontSize="sm"
            color="smPrimary"
          >
            {t("{{count}} of {{total}} products have a procurement", {
              count: summary.products.withProcurement,
              total: summary.products.total,
            })}
          </Link>
        </>
      )}
    </Box>
  );
};

const ContractFigure = ({ label, value, to }: { label: string; value: ContractValueDto; to: string }) => {
  const { t } = useTranslation();
  return (
    <Figure
      label={label}
      figure={contractValueFigure(value)}
      detail={t("{{count}} contracts", { count: value.contracts })}
      note={
        [
          value.withoutTotalPrice > 0
            ? t("{{count}} contracts without a total price are not included", { count: value.withoutTotalPrice })
            : null,
          value.drafts ? t("{{count}} draft contracts are not included", { count: value.drafts }) : null,
        ]
          .filter(Boolean)
          .join(" · ") || null
      }
      to={to}
    />
  );
};

const UNKNOWN_VALUE = "–";

export const contractValueFigure = (value: ContractValueDto): string => {
  if (value.totals.length === 0) {
    return value.contracts > 0 ? UNKNOWN_VALUE : displaySum({ value: 0, currency: "NOK" });
  }
  return value.totals.map((total) => displaySum({ value: total.amount, currency: total.currency })).join(" + ");
};

const Figure = ({
  label,
  figure,
  detail,
  note,
  to,
}: {
  label: string;
  figure: string;
  detail?: string | null;
  note?: string | null;
  to: string;
}) => (
  <Link
    as={RouterLink}
    to={to}
    display="block"
    border="1px solid"
    borderColor="smBorder"
    rounded="md"
    p="3"
    _hover={{ borderColor: "smPrimary", textDecoration: "none" }}
  >
    <Text fontSize="sm" color="smMuted">
      {label}
    </Text>
    <Text fontSize="xl" fontWeight="semibold" color="smText">
      {figure}
    </Text>
    {detail && (
      <Text fontSize="sm" color="smMuted">
        {detail}
      </Text>
    )}
    {note && (
      <Text fontSize="xs" color="smMuted" mt="1">
        {note}
      </Text>
    )}
  </Link>
);
